import { useQuery } from "@tanstack/react-query";
import { Card, Callout, Spinner, Badge, Table, type Column } from "@/design/components";
import { ApiError } from "@/api/client";
import { listOperatorAudit, type OperatorAuditEntry } from "@/api/operator/audit";
import styles from "./OperatorAuditScreen.module.css";

const apiMessage = (err: unknown, fallback: string): string => (err instanceof ApiError ? err.message : fallback);

const ACTION_LABEL: Record<string, string> = {
  "org.suspended": "Org suspended",
  "org.reactivated": "Org reactivated",
  "billing.refunded": "Refund issued",
  "org.impersonated": "Impersonation",
};
const actionLabel = (a: string): string => ACTION_LABEL[a] ?? a;

type Tone = "accent" | "neutral" | "success" | "warning" | "danger";
const ACTION_TONE: Record<string, Tone> = {
  "org.suspended": "warning",
  "org.reactivated": "success",
  "billing.refunded": "accent",
  "org.impersonated": "danger",
};
const actionTone = (a: string): Tone => ACTION_TONE[a] ?? "neutral";

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}
function detailSummary(detail: Record<string, unknown>): string {
  const parts: string[] = [];
  for (const [k, v] of Object.entries(detail)) {
    if (v === null || v === undefined || typeof v === "object") continue;
    parts.push(`${k}: ${String(v)}`);
  }
  return parts.join(" · ");
}

/** Platform-wide operator action trail (suspend/reactivate/refund/impersonate). */
export function OperatorAuditScreen() {
  const query = useQuery({ queryKey: ["operator", "audit"], queryFn: ({ signal }) => listOperatorAudit({ limit: 100 }, signal) });
  const entries = query.data ?? [];

  const columns: Column<OperatorAuditEntry>[] = [
    { key: "when", header: "When", render: (e) => <span className={styles.when}>{formatDateTime(e.createdAt)}</span> },
    { key: "action", header: "Action", render: (e) => <Badge tone={actionTone(e.action)}>{actionLabel(e.action)}</Badge> },
    { key: "by", header: "Operator", render: (e) => <span>{e.operatorEmail ?? `${e.operatorId.slice(0, 8)}…`}</span> },
    { key: "org", header: "Org", render: (e) => <span className={styles.mono}>{e.orgId ? `${e.orgId.slice(0, 8)}…` : "—"}</span> },
    { key: "detail", header: "Details", render: (e) => <span className={styles.detail}>{detailSummary(e.detail) || "—"}</span> },
  ];

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <div>
          <p className="u-eyebrow">Platform</p>
          <h1 className={styles.h1}>Activity</h1>
        </div>
      </header>
      {query.isPending && (
        <div className={styles.loading}><Spinner size={20} color="var(--accent)" /> <span>Loading activity…</span></div>
      )}
      {query.isError && <Callout tone="danger" title="Couldn't load activity">{apiMessage(query.error, "Please try again shortly.")}</Callout>}
      {query.data && (
        <Card className={styles.tableCard}>
          <Table columns={columns} rows={entries} getRowKey={(e) => e.id} emptyText="No operator actions recorded yet." />
        </Card>
      )}
    </div>
  );
}
